import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { adsApi } from '@/api/client';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { BudgetUsageEntityPanel } from '@/components/dashboard/BudgetUsageEntityPanel';
import type { Portfolio } from '@/types/portfolios';

import { formatCurrency as formatAccountCurrency } from '@/utils/formatCurrency';

function formatCurrency(amount: number, currencyCode?: string) {
  return formatAccountCurrency(amount, currencyCode ? { currencyCode } : undefined);
}

function formatDateValue(value: unknown): string {
  if (value == null || value === '') return '—';
  const text = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return text;
  return parsed.toISOString().slice(0, 10);
}

function formatDateTimeValue(value: unknown): string {
  if (value == null || value === '') return '—';
  const parsed = new Date(String(value));
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString();
}

function formatPolicyLabel(policy: unknown): string {
  if (policy == null || policy === '') return 'No cap';
  return String(policy)
    .split('_')
    .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
    .join(' ');
}

function portfolioBudgetLabel(portfolio: Portfolio): string {
  const budget = portfolio.budget;
  if (!budget || budget.policy === 'NO_CAP' || budget.amount == null) {
    return 'No cap';
  }
  return formatCurrency(budget.amount, budget.currencyCode);
}

function PortfolioStateBadge({ state }: { state: string }) {
  const normalized = state.toUpperCase();
  const variant =
    normalized === 'ENABLED' ? 'success' : normalized === 'PAUSED' ? 'warning' : 'neutral';
  return <Badge variant={variant}>{normalized}</Badge>;
}

function InBudgetBadge({ inBudget }: { inBudget?: boolean }) {
  if (inBudget == null) return <span className="text-foreground-muted">—</span>;
  return (
    <Badge variant={inBudget ? 'success' : 'warning'}>{inBudget ? 'In budget' : 'Out of budget'}</Badge>
  );
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 border-b border-border py-2 last:border-b-0">
      <dt className="text-body-sm font-medium text-foreground-muted">{label}</dt>
      <dd className="text-body-sm text-foreground break-words">{value}</dd>
    </div>
  );
}

function PortfolioDetailModal({
  profileId,
  portfolio,
  onClose,
}: {
  profileId: string;
  portfolio: Portfolio;
  onClose: () => void;
}) {
  const budget = portfolio.budget;
  const extended = portfolio.extendedData;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-surface p-6 shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="portfolio-detail-title"
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h3 id="portfolio-detail-title" className="text-lg font-semibold text-foreground">
              {portfolio.name}
            </h3>
            <p className="mt-1 font-mono text-xs text-foreground-muted">ID: {portfolio.portfolioId}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-pill p-1 text-foreground-muted hover:bg-surface-muted hover:text-foreground"
            aria-label="Close portfolio details"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <dl className="rounded-xl border border-border bg-surface-muted/40 px-4">
          <DetailRow label="State" value={<PortfolioStateBadge state={portfolio.state} />} />
          <DetailRow label="In budget" value={<InBudgetBadge inBudget={portfolio.inBudget} />} />
          <DetailRow label="Budget policy" value={formatPolicyLabel(budget?.policy)} />
          <DetailRow label="Budget amount" value={portfolioBudgetLabel(portfolio)} />
          <DetailRow label="Currency" value={budget?.currencyCode || '—'} />
          <DetailRow label="Budget start" value={formatDateValue(budget?.startDate)} />
          <DetailRow label="Budget end" value={formatDateValue(budget?.endDate)} />
          <DetailRow label="Serving status" value={extended?.servingStatus || '—'} />
          <DetailRow label="Created" value={formatDateTimeValue(extended?.creationDateTime)} />
          <DetailRow label="Last updated" value={formatDateTimeValue(extended?.lastUpdateDateTime)} />
          <DetailRow
            label="Status reasons"
            value={
              extended?.statusReasons?.length ? extended.statusReasons.join(', ') : '—'
            }
          />
        </dl>

        <div className="mt-6 border-t border-border pt-4">
          <h4 className="mb-3 text-body-sm font-semibold text-foreground">Budget usage alerts</h4>
          <BudgetUsageEntityPanel
            profileId={profileId}
            portfolioId={portfolio.portfolioId}
            entityLabel={portfolio.name}
          />
        </div>

        <div className="mt-4">
          <p className="mb-2 text-body-sm font-medium text-foreground-muted">Raw API payload</p>
          <pre className="max-h-64 overflow-auto rounded-xl border border-border bg-surface-muted p-3 text-xs text-foreground">
            {JSON.stringify(portfolio, null, 2)}
          </pre>
        </div>
      </div>
    </div>
  );
}

export function PortfoliosSection({ profileId }: { profileId: string }) {
  const [search, setSearch] = useState('');
  const [selectedPortfolio, setSelectedPortfolio] = useState<Portfolio | null>(null);

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['profile-portfolios', profileId],
    queryFn: async () => {
      const res = await adsApi.listPortfolios(profileId);
      if (!res.success || !res.data) {
        throw new Error('Failed to load portfolios');
      }
      return res.data;
    },
    enabled: Boolean(profileId),
    staleTime: 0,
  });

  const portfolios = data?.portfolios ?? [];

  const filteredPortfolios = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return portfolios;
    return portfolios.filter((portfolio) => {
      const haystack = [portfolio.name, portfolio.portfolioId, portfolio.state]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [portfolios, search]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-body-sm text-foreground-muted">
        <Spinner className="h-4 w-4" />
        Loading portfolios…
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-xl border border-error/20 bg-error-subtle px-4 py-3 text-body-sm text-error-ink">
        {error instanceof Error ? error.message : 'Failed to load portfolios.'}
        <button
          type="button"
          onClick={() => void refetch()}
          className="ml-3 font-medium underline"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-border bg-surface p-4 shadow-sm">
        <p className="text-body-sm text-foreground-muted">
          {filteredPortfolios.length} portfolio{filteredPortfolios.length !== 1 ? 's' : ''}
          {data?.totalResults != null ? ` · ${data.totalResults} total` : ''}
        </p>
        <div className="min-w-[220px] flex-1">
          <label htmlFor="portfolio-search" className="sr-only">
            Search portfolios
          </label>
          <input
            id="portfolio-search"
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or ID"
            autoComplete="off"
            className="w-full rounded-lg border border-border bg-surface-muted px-3 py-2 text-body-sm placeholder:text-foreground-muted focus:outline-none focus:ring-2 focus:ring-brand/30"
          />
        </div>
        <button
          type="button"
          onClick={() => void refetch()}
          disabled={isFetching}
          className="rounded-lg border border-border px-3 py-2 text-body-sm text-foreground-muted hover:bg-surface-muted disabled:opacity-50"
        >
          {isFetching ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <div className="bg-surface border border-border rounded-xl shadow-sm overflow-hidden">
        <div className="max-h-96 overflow-auto custom-scrollbar">
          <table className="min-w-full text-body-sm">
            <thead className="bg-surface-muted border-b border-border sticky top-0 z-10">
              <tr>
                <th className="px-4 py-2 text-xs font-bold uppercase tracking-wide text-foreground-muted text-left">
                  Portfolio name
                </th>
                <th className="px-4 py-2 text-xs font-bold uppercase tracking-wide text-foreground-muted text-left">
                  State
                </th>
                <th className="px-4 py-2 text-xs font-bold uppercase tracking-wide text-foreground-muted text-left">
                  Budget
                </th>
                <th className="px-4 py-2 text-xs font-bold uppercase tracking-wide text-foreground-muted text-left">
                  Policy
                </th>
                <th className="px-4 py-2 text-xs font-bold uppercase tracking-wide text-foreground-muted text-left">
                  In budget
                </th>
                <th className="px-4 py-2 text-xs font-bold uppercase tracking-wide text-foreground-muted text-left">
                  Serving status
                </th>
                <th className="px-4 py-2 text-xs font-bold uppercase tracking-wide text-foreground-muted text-center">
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredPortfolios.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-foreground-muted">
                    No portfolios found.
                  </td>
                </tr>
              ) : (
                filteredPortfolios.map((portfolio) => (
                  <tr
                    key={portfolio.portfolioId}
                    className="transition-colors hover:bg-surface-muted/80"
                  >
                    <td className="px-4 py-3 min-w-[220px]">
                      <button
                        type="button"
                        onClick={() => setSelectedPortfolio(portfolio)}
                        className="block text-left font-bold text-brand hover:underline"
                      >
                        {portfolio.name}
                      </button>
                      <span className="mt-0.5 block font-mono text-[10px] text-foreground-muted">
                        ID: {portfolio.portfolioId}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <PortfolioStateBadge state={portfolio.state} />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap font-medium">
                      {portfolioBudgetLabel(portfolio)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-foreground-muted">
                      {formatPolicyLabel(portfolio.budget?.policy)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <InBudgetBadge inBudget={portfolio.inBudget} />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-foreground-muted">
                      {portfolio.extendedData?.servingStatus || '—'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        type="button"
                        onClick={() => setSelectedPortfolio(portfolio)}
                        className="rounded-pill bg-brand-subtle px-3 py-1.5 text-xs font-semibold text-brand hover:bg-brand-subtle/80"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedPortfolio ? (
        <PortfolioDetailModal
          profileId={profileId}
          portfolio={selectedPortfolio}
          onClose={() => setSelectedPortfolio(null)}
        />
      ) : null}
    </>
  );
}
